import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ClipboardDocumentListIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import { guardianApi } from '../../services/api/guardian';
import { useGuardianAuthStore } from '../../stores/guardianAuthStore';
import { Card, H1, H3, Text, Badge, Button, Spinner, Alert } from '../../components/ui';
import { formatDate } from '../../utils/date';

export const GuardianObservations: React.FC = () => {
  const { children: storeChildren } = useGuardianAuthStore();
  const [selectedChildId, setSelectedChildId] = useState<string | 'all'>('all');

  const { data: fetchedChildren } = useQuery({
    queryKey: ['guardian-children'],
    queryFn: guardianApi.portal.getChildren,
    enabled: !storeChildren || storeChildren.length === 0,
  });

  const childList = storeChildren && storeChildren.length > 0 ? storeChildren : fetchedChildren || [];

  const { data: observations, isLoading, error } = useQuery({
    queryKey: ['guardian-all-observations', selectedChildId, childList.map((c: any) => c.id)],
    queryFn: async () => {
      const targetChildren = selectedChildId === 'all'
        ? childList
        : childList.filter((c: any) => c.id === selectedChildId);

      const allObs = await Promise.all(
        targetChildren.map((child: any) =>
          guardianApi.portal.getChildObservations(child.id, { limit: 100 })
            .then(obs => obs.map((o: any) => ({
              ...o,
              childName: `${child.first_name} ${child.last_name}`,
            })))
        )
      );

      return allObs
        .flat()
        .sort((a: any, b: any) => new Date(b.observation_date).getTime() - new Date(a.observation_date).getTime());
    },
    enabled: childList.length > 0,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="error">
        Failed to load observations. Please try again later.
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <H1>All Observations</H1>
        <Text className="mt-2 text-gray-600 dark:text-gray-400">
          View all published observations for your children
        </Text>
      </div>

      {childList.length > 1 && (
        <Card className="p-4">
          <div className="flex items-center flex-wrap gap-3">
            <FunnelIcon className="w-5 h-5 text-gray-400 dark:text-gray-500" />
            <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">Filter by child:</Text>
            <div className="flex flex-wrap gap-2">
              <Button
                variant={selectedChildId === 'all' ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setSelectedChildId('all')}
              >
                All Children
              </Button>
              {childList.map((child: any) => (
                <Button
                  key={child.id}
                  variant={selectedChildId === child.id ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedChildId(child.id)}
                >
                  {child.first_name}
                </Button>
              ))}
            </div>
          </div>
        </Card>
      )}

      <div className="space-y-4">
        {observations && observations.length > 0 ? (
          observations.map((observation: any) => (
            <Card key={observation.id} className="p-6">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-2">
                  {childList.length > 1 && (
                    <Badge variant="primary" size="sm">
                      {observation.childName}
                    </Badge>
                  )}
                  {observation.category && (
                    <Badge variant="secondary" size="sm">
                      {observation.category}
                    </Badge>
                  )}
                </div>
                <Text className="text-sm text-gray-500 dark:text-gray-400">
                  {formatDate(observation.observation_date)}
                </Text>
              </div>

              <Text className="text-gray-700 dark:text-gray-300 mb-3">
                {observation.narrative}
              </Text>

              {observation.media && observation.media.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {observation.media.map((media: any) => (
                    media.media_type === 'image' && (
                      <img
                        key={media.id}
                        src={media.file_path}
                        alt={media.caption || 'Observation photo'}
                        className="w-24 h-24 object-cover rounded-lg"
                      />
                    )
                  ))}
                </div>
              )}

              <div className="flex items-center justify-between text-sm">
                <Text className="text-gray-500 dark:text-gray-400">
                  By: {observation.teacher_name || `${observation.teacher?.first_name || ''} ${observation.teacher?.last_name || ''}`.trim() || 'Unknown'}
                </Text>
                {observation.learning_area && (
                  <Badge variant="info" size="sm">
                    {typeof observation.learning_area === 'string'
                      ? observation.learning_area
                      : observation.learning_area?.name}
                  </Badge>
                )}
              </div>
            </Card>
          ))
        ) : (
          <Card className="p-8">
            <div className="text-center">
              <ClipboardDocumentListIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
              <H3 className="mt-4 text-gray-900 dark:text-gray-100">No Observations Yet</H3>
              <Text className="mt-2 text-gray-500 dark:text-gray-400">
                No published observations available for your children.
              </Text>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};
